import '../upsert-collection-layer/upsert-collection-layer.js';
import '../wishlist-collection/wishlist-collection.js';
import { classMap } from 'lit/directives/class-map.js';

import { CSSResult, html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { Messages } from '../../../@types/wishlist.js';
import { consume } from '@lit/context';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { repeat } from 'lit/directives/repeat.js';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import styles from './wishlist_collections.scss?inline';
import {
  collectionsContext,
  curatedCollectionContext,
  productsContext,
} from '../../../scripts/context/wishlistContext.js';
import { CollectionsState, CuratedCollectionsState, ProductsState } from '../../../scripts/context/wishlist-state.js';
import {
  TrackedCuratedCollection,
  trackPageView,
  trackViewCollections,
  trackViewCuratedCollections,
  trackViewWishlist,
} from '../../../scripts/service/cube-tracking-layer.js';
import { CollectionMenuState } from '../../../scripts/context/menu-state.js';
import { collectionMenuContext } from '../../../scripts/context/menuContext.js';
import { WishlistAppConfig, wishlistAppConfigContext } from '../../../scripts/context/appConfigContext.js';
import checkmarkIcon from '@breuni-ui/design-tokens/images/icons/o-ic-16_check.svg?element';
import { pushToSnackbar } from '../../../scripts/service/snackbar-service.js';
import { DetailsError } from '../wishlist-collection-details-error/wishlist-collection-details-error.js';
import { addTitleToIcon } from '../icon/icon-accessibility.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

@customElement('ents-wishlist-collections')
export class WishlistCollections extends LitElement {
  @property({ type: Boolean, attribute: 'logged-in' })
  private loggedIn: boolean = false;

  @property({ type: Boolean, attribute: 'from-no-longer-shared-collection' })
  private fromNoLongerSharedCollection: boolean = false;

  @consume({ context: collectionsContext, subscribe: true })
  @property({ attribute: false })
  private collectionsState!: CollectionsState;

  @consume({ context: productsContext, subscribe: true })
  @property({ attribute: false })
  private productsState!: ProductsState;

  @consume({ context: collectionMenuContext, subscribe: true })
  @property({ attribute: false })
  private collectionMenuState!: CollectionMenuState;

  @consume({ context: curatedCollectionContext })
  private curatedCollectionsState!: CuratedCollectionsState;

  @consume({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({ context: i18nContext })
  private messages!: Messages;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  connectedCallback() {
    super.connectedCallback();

    this.preloadCuratedCollectionsForTrackingIfNeeded().then(() => {
      setTimeout(() => this.trackPageEvents());
    });
  }

  private async preloadCuratedCollectionsForTrackingIfNeeded() {
    // Preload curated collections for tracking
    if (this.wishlistAppConfig.hasCuratedCollections) {
      await this.curatedCollectionsState.getAll(this.wishlistAppConfig.curatedCollectionApiEndpoint);
    }
  }

  private trackPageEvents() {
    trackPageView('collection_list', this.collectionsState.entities.length === 0, this.loggedIn);
    trackViewWishlist('collection', this.productsState.entities);
    trackViewCollections('collection', this.collectionsState.entities, this.productsState);

    const allCuratedCollections = this.curatedCollectionsState.entities;
    const curatedCollectionsToTrack: TrackedCuratedCollection[] = allCuratedCollections.map((entry) => ({
      collection: entry.collection,
      quantity: entry.products.length,
    }));
    trackViewCuratedCollections('collection', curatedCollectionsToTrack);
  }

  private openCreateCollectionLayer() {
    this.collectionMenuState.openCreateLayer();
  }

  protected renderNoLongerSharedWishlistHint() {
    if (!this.fromNoLongerSharedCollection) {
      return nothing;
    }
    if (this.loggedIn) {
      pushToSnackbar(`${this.messages['ents_wishlist_collection_details_not_exist_snackbar_text']}`);
      return nothing;
    }
    return html`
      <ents-wishlist-collection-details-error .error="${DetailsError.COLLECTION_NOT_EXISTS}">
      </ents-wishlist-collection-details-error>
    `;
  }

  protected renderLoginHint() {
    return html`
      <ents-wishlist-collection-details-error .error="${DetailsError.NOT_LOGGED_IN}">
      </ents-wishlist-collection-details-error>
      ${this.renderNoLongerSharedWishlistHint()}
    `;
  }

  render() {
    if (!this.loggedIn) {
      return this.renderLoginHint();
    }
    return html`
      <div class="ents-wishlist-collection-container">
        ${this.renderNoLongerSharedWishlistHint()}
        <div
          class="ents-wishlist-collection-actions
        ${classMap({
            'ents-wishlist-collection-actions__empty': this.collectionsState.entities.length === 0,
          })}"
        >
          <button
            class="bdk-button"
            variant="outline"
            size="medium"
            full-width
            @click="${this.openCreateCollectionLayer}"
            data-test-ents-wishlist-collection-create-button
          >
            ${this.messages['ents_wishlist_collection_open_create_layer_button']}
          </button>
        </div>
        ${this.collectionsState.entities.length === 0 ? this.renderEmptyCollections() : this.renderCollections()}
      </div>
    `;
  }

  private renderEmptyCollections() {
    return html`
      <div class="ents-wishlist-collection-lists ents-wishlist-collection-lists__empty ents-wishlist-collection-grid">
        <div class="ents-wishlist-collection-lists__empty--inner">
          <h3 class="ents-copy-medium-bold" data-test-ents-wishlist-collection-empty-hint>
            ${this.messages['ents_wishlist_collections_empty_headline']}
          </h3>
          <div class="ents-wishlist-collection-lists__empty--text ents-prose-copy-small">
            <div class="ents-wishlist-collection-lists__empty--icon">
              ${addTitleToIcon(checkmarkIcon(), this.messages['ents_wishlist_collections_empty_text1'])}
            </div>
            ${unsafeHTML(`<span>${this.messages['ents_wishlist_collections_empty_text1']}</span>`)}
          </div>
          <div class="ents-wishlist-collection-lists__empty--text ents-prose-copy-small">
            <div class="ents-wishlist-collection-lists__empty--icon">
              ${addTitleToIcon(checkmarkIcon(), this.messages['ents_wishlist_collections_empty_text2'])}
            </div>
            ${this.messages['ents_wishlist_collections_empty_text2']}
          </div>
          <div class="ents-wishlist-collection-lists__empty--text ents-prose-copy-small">
            <div class="ents-wishlist-collection-lists__empty--icon">
              ${addTitleToIcon(checkmarkIcon(), this.messages['ents_wishlist_collections_empty_text3'])}
            </div>
            ${this.messages['ents_wishlist_collections_empty_text3']}
          </div>
        </div>
      </div>
    `;
  }

  private renderCollections() {
    return html`
      <div class="ents-wishlist-collection-lists ents-wishlist-collection-grid">
        ${repeat(
          this.collectionsState.entities,
          (col) => col.id,
          (col) => html`
            <article>
              <ents-wishlist-collection
                .wishlistCollection="${col}"
                .products="${this.productsState.getProductsToRender(col.id)}"
              />
            </article>
          `,
        )}
      </div>
    `;
  }
}
